import { z } from "zod";
import { setVendorLabel } from "../portals/messagemedia/theme";

export const UniversalBootstrapOpts = z
  .object({
    projectIdHeader: z
      .boolean()
      .default(false)
      .describe(
        "Whether or not to add the project id header to requests. Used in Sinch Engage to decide what project the user is interacting with"
      ),
    googleTagManager: z
      .boolean()
      .default(false)
      .describe("Whether or not to initialize Google Tag Manager"),
    rudderstack: z
      .object({
        startSession: z
          .boolean()
          .optional()
          .default(false)
          .describe("Whether or not to start a session at the bootstrap"),
        dataPlaneUrl: z.string(),
        writeKey: z.string(),
      })
      .or(z.null())
      .default(null)
      .describe("Whether or not to initialize Rudderstack"),
    exceptionMonitoring: z
      .boolean()
      .default(false)
      .describe(
        "Whether or not to initialize exception monitoring with datadog"
      ),
    messageMediaThemes: z
      .boolean()
      .default(false)
      .describe("Whether or not to initialize the MessageMedia themes"),
    patchRequestsWithAuth: z
      .boolean()
      .default(false)
      .describe("Whether or not to patch requests with auth headers"),
    requireCriticalBrowserSupport: z
      .boolean()
      .default(false)
      .describe(
        "Whether or not to require critical browser support. If the browser does not support the critical features, the user will be redirected to a page explaining the issue."
      ),
    setDefaultLanguageToBrowserDefault: z
      .boolean()
      .default(false)
      .describe("Whether or not to set the browser language as a default"),
    extraNectaryTheme: z
      .string()
      .nullable()
      .default(null)
      .describe("An optional extra nectary theme to be installed"),
    withoutShadowRoot: z
      .boolean()
      .default(true)
      .describe("Whether or not to render the app without a shadow root"),
    handleUtmParameters: z
      .boolean()
      .default(false)
      .describe("Whether utm params should be handled"),
    extraWindowValues: z
      .array(z.object({ key: z.string(), value: z.unknown() }))
      .default([])
      .describe("Values that should be added to the window object."),
    hotjarId: z
      .string()
      .nullable()
      .default(null)
      .describe(
        "Id for Hotjar to be loaded, null if it shouldn' be loaded on this portal."
      ),
  })
  .default({});

export type UniversalBootstrapOpts = z.infer<typeof UniversalBootstrapOpts>;

export type EntryName =
  | "mm"
  | "engage"
  | "cop"
  | "my"
  | "account"
  | "accountAdmin"
  | "dashboard"
  | "connect";
export type EntryMapping<T> = { [K in EntryName]: T };

export type ShellEnvFromHTML = {
  MFE_CAT_URL: string;
  CONFIG_NAME: string;
  ENV: "prod" | "stg" | "qa" | "dev" | "local";
  WITHOUT_SHADOW_ROOT: boolean;
};
export type PossiblyIncompleteShellEnv =
  | ShellEnvFromHTML
  | typeof window.SHELL_ENV;

export type AuthConfig =
  | {
      type: "kc" | "mm" | "tbi" | "dashboard";
      url?: string;
      publicPaths: string[];
    }
  | {
      type: "oidc";
      url?: string;
      aud?: string;
      clientId?: string;
      publicPaths: string[];
    }
  | {
      type: "azure";
      url?: never;
      tenantId: string;
      clientId: string;
      authority?: string;
      redirectUri?: string;
      scopes?: string[];
      publicPaths: string[];
    };

export type FinalShellEnv = ShellEnvFromHTML & {
  AUTH_CONFIG: AuthConfig;
  API_STRATEGY: "engage" | "mm" | "cop";
  CONFIGCAT_SDK_KEY: string;
  DATADOG_APPLICATION_ID: string;
  DATADOG_CLIENT_TOKEN: string;
  API_BASE_URL: string;
  NEXTGEN_API_URL: string;
  ENGAGE_MFE_BASE_DOMAIN: string;
  SOCKET_HOST?: string; // Can potentially move into configcat
  CAMPAIGNS_HIVE_PROXY?: string;
  COMMIT_HASH: string;
  UNIVERSAL_BOOTSTRAP_OPTIONS?: UniversalBootstrapOpts;
  SENTRY_DSN?: string;
  SENTRY_PERFORMANCE_SAMPLE_RATE?: number;
  SENTRY_ERROR_SAMPLE_RATE?: number;
  DATADOG_LOGS_CLIENT_TOKEN?: string;
  plugins?: string[];
};

function entryFromEnv(env: PossiblyIncompleteShellEnv): EntryName {
  const configName = env.CONFIG_NAME;
  const entry = configName.split(".")[0];
  if (
    entry === "mm" ||
    entry === "engage" ||
    entry === "cop" ||
    entry === "dashboard" ||
    entry === "my" ||
    entry === "accountAdmin" ||
    entry === "account" ||
    entry === "connect"
  ) {
    return entry;
  }
  throw new Error(`Unknown ENTRY: ${entry}`);
}

/**
 * Represents the configuration settings for the application.
 */
class Config {
  public constructor(private fn: () => FinalShellEnv) {}

  /**
   * The complete Shell environment.
   */
  public get SHELL_ENV(): FinalShellEnv {
    return this.fn();
  }

  /**
   * The Sentry DSN for error monitoring or null if not set.
   */
  public get SENTRY_DSN(): string | null {
    return this.SHELL_ENV.SENTRY_DSN ?? null;
  }

  /**
   * The Sentry DSN for error monitoring or null if not set.
   */
  public get SENTRY_ERROR_SAMPLE_RATE(): number | null {
    return this.SHELL_ENV.SENTRY_ERROR_SAMPLE_RATE ?? null;
  }

  public get SENTRY_PERFORMANCE_SAMPLE_RATE(): number | null {
    return this.SHELL_ENV.SENTRY_PERFORMANCE_SAMPLE_RATE ?? null;
  }

  /**
   * The datadog logs client token.
   */
  public get DATADOG_LOGS_CLIENT_TOKEN(): string | null {
    return this.SHELL_ENV.DATADOG_LOGS_CLIENT_TOKEN ?? null;
  }

  /**
   * The base domain for Engae MFEs
   */
  public get VENDOR_LABEL(): string | undefined {
    if (!window.VENDOR_LABEL && this.getEntry() === "mm") {
      setVendorLabel();
    }
    return window.VENDOR_LABEL;
  }

  /**
   * The base domain for Engae MFEs
   */
  public get ENGAGE_MFE_BASE_DOMAIN(): string {
    return this.SHELL_ENV?.ENGAGE_MFE_BASE_DOMAIN;
  }

  /**
   * The Datadog application ID for datadog
   */
  public get DATADOG_APPLICATION_ID(): string {
    return this.SHELL_ENV?.DATADOG_APPLICATION_ID;
  }

  /**
   * The Datadog client token.
   */
  public get DATADOG_CLIENT_TOKEN(): string {
    return this.SHELL_ENV.DATADOG_CLIENT_TOKEN;
  }

  /**
   * The authentication configuration.
   */
  public get AUTH_CONFIG(): AuthConfig {
    return this.SHELL_ENV.AUTH_CONFIG;
  }

  /**
   * The base URL for API requests.
   */
  public get API_BASE_URL(): string {
    return this.SHELL_ENV.API_BASE_URL;
  }

  /**
   * The ConfigCat SDK key.
   */
  public get CONFIGCAT_SDK_KEY(): string {
    return this.SHELL_ENV.CONFIGCAT_SDK_KEY;
  }

  /**
   * The socket host.
   */
  public get SOCKET_HOST(): string | undefined {
    return this.SHELL_ENV.SOCKET_HOST;
  }

  /**
   * The campaigns Hive proxy.
   */
  public get CAMPAIGNS_HIVE_PROXY(): string | undefined {
    return this.SHELL_ENV.CAMPAIGNS_HIVE_PROXY;
  }

  /**
   * The MFECat URL.
   */
  public get MFE_CAT_URL(): string {
    return this.SHELL_ENV.MFE_CAT_URL;
  }

  public get API_STRATEGY(): "engage" | "mm" | "cop" | "dashboard" {
    return this.SHELL_ENV.API_STRATEGY;
  }

  public getEntry(): EntryName {
    return entryFromEnv(this.SHELL_ENV);
  }

  private parsedUniversalBootstrapOptions: UniversalBootstrapOpts | null = null;

  public get universalBootstrapOptions(): UniversalBootstrapOpts {
    if (!this.parsedUniversalBootstrapOptions) {
      const opts = UniversalBootstrapOpts.parse(
        this.SHELL_ENV.UNIVERSAL_BOOTSTRAP_OPTIONS
      );
      this.parsedUniversalBootstrapOptions = opts;
      return opts;
    } else {
      return this.parsedUniversalBootstrapOptions;
    }
  }

  /**
   * The plugins array from the config.
   */
  public get plugins(): string[] {
    return this.SHELL_ENV.plugins ?? [];
  }

  /**
   * Takes an object that which has keys matching EntryNames and returns the value of the key that matches the current entry.
   *
   * @param obj The object to select from.
   */
  public selectByEntry<T>(obj: { [K in EntryName]: T }): T {
    return obj[this.getEntry()];
  }
}

// Just export the type Config, not the class Config itself
export type { Config };

export const config = new Config(() => window.SHELL_ENV);
